/** Character template + scene saver persistence with prompt auto-improvement. */
import { nanoid } from 'nanoid';
import { db, type CharacterTemplate, type SceneSaver, type Attachment } from './db';
import { supabaseEnabled, supabase, currentUser } from './supabase';
import { proxy } from '../providers/common';
import { getKey } from './keys';
import { DIRECTOR_PERSONA } from './persona';

async function improvePrompt(seed: string, refs: Attachment[]): Promise<string> {
  const geminiKey = await getKey('gemini');
  if (!geminiKey) return seed;
  const parts: any[] = [
    { text: `${DIRECTOR_PERSONA}\n\nRewrite this into a tight, model-ready character or scene template. Output only the template text.\n\nSeed: ${seed}` }
  ];
  for (const r of refs.slice(0, 4)) {
    const b64 = await blobToB64(r.blob);
    parts.push({ inlineData: { mimeType: r.mime, data: b64 } });
  }
  try {
    const res = await proxy<any>({
      provider: 'gemini',
      endpoint: 'https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent',
      body: { contents: [{ role: 'user', parts }], generationConfig: { temperature: 0.3 } },
      apiKey: geminiKey,
      authStyle: 'x-goog-api-key'
    });
    return res.candidates?.[0]?.content?.parts?.[0]?.text?.trim() || seed;
  } catch { return seed; }
}

async function blobToB64(b: Blob): Promise<string> {
  return await new Promise((res, rej) => {
    const fr = new FileReader();
    fr.onload = () => res(String(fr.result).split(',')[1]);
    fr.onerror = () => rej(fr.error);
    fr.readAsDataURL(b);
  });
}

export async function saveCharacter(input: { name: string; seedPrompt: string; refs: Attachment[]; notes?: string }): Promise<CharacterTemplate> {
  const improved = await improvePrompt(input.seedPrompt, input.refs);
  const c: CharacterTemplate = {
    id: nanoid(), name: input.name, prompt: improved, refs: input.refs, notes: input.notes,
    createdAt: Date.now(), updatedAt: Date.now()
  };
  (await db()).put('characters', c);
  return c;
}

export async function saveScene(input: { name: string; seedPrompt: string; refs: Attachment[]; cameraNotes?: string; lightingNotes?: string }): Promise<SceneSaver> {
  const improved = await improvePrompt(input.seedPrompt, input.refs);
  const s: SceneSaver = {
    id: nanoid(), name: input.name, prompt: improved, refs: input.refs,
    cameraNotes: input.cameraNotes, lightingNotes: input.lightingNotes,
    createdAt: Date.now(), updatedAt: Date.now()
  };
  (await db()).put('scenes', s);
  return s;
}

export async function saveTemplate(input: {
  name: string;
  seedPrompt: string;
  category: string;
  tags: string[];
  refs: Attachment[];
  code?: string;
  deployCommand?: string;
  startFrame?: Attachment | null;
  endFrame?: Attachment | null;
  isPublic?: boolean;
}): Promise<any> {
  const improved = await improvePrompt(input.seedPrompt, input.refs);
  const t = {
    id: nanoid(),
    name: input.name,
    prompt: improved,
    category: input.category,
    tags: input.tags,
    refs: input.refs,
    code: input.code,
    deployCommand: input.deployCommand,
    startFrame: input.startFrame,
    endFrame: input.endFrame,
    isPublic: input.isPublic,
    createdAt: Date.now(),
    updatedAt: Date.now()
  };

  const storeName = input.category === 'characters' ? 'characters' : 'scenes';
  (await db()).put(storeName, t as any);

  if (supabaseEnabled && supabase) {
    currentUser().then(async (user) => {
      if (!user) return;
      try {
        const uploadedRefs = await Promise.all((input.refs || []).map(async (r) => {
          try {
            const path = `${user.id}/${r.id}`;
            await supabase!.storage.from('protolive-assets').upload(path, r.blob, { upsert: true, contentType: r.mime });
            return path;
          } catch { return r.id; }
        }));

        let startFramePath = null;
        if (input.startFrame) {
          try {
            startFramePath = `${user.id}/${input.startFrame.id}`;
            await supabase!.storage.from('protolive-assets').upload(startFramePath, input.startFrame.blob, { upsert: true, contentType: input.startFrame.mime });
          } catch {}
        }

        let endFramePath = null;
        if (input.endFrame) {
          try {
            endFramePath = `${user.id}/${input.endFrame.id}`;
            await supabase!.storage.from('protolive-assets').upload(endFramePath, input.endFrame.blob, { upsert: true, contentType: input.endFrame.mime });
          } catch {}
        }

        await supabase!.from('templates').upsert({
          id: t.id,
          user_id: user.id,
          name: t.name,
          prompt: t.prompt,
          category: t.category,
          tags: t.tags,
          code: t.code || null,
          deploy_command: t.deployCommand || null,
          is_public: t.isPublic ?? false,
          status: t.isPublic ? 'pending' : 'approved',
          start_frame: startFramePath,
          end_frame: endFramePath,
          refs: uploadedRefs,
          updated_at: new Date(t.updatedAt).toISOString()
        });
      } catch (err) {
        console.warn('Supabase templates sync error:', err);
      }
    });
  }

  return t;
}

export async function listTemplates(category: string): Promise<any[]> {
  const storeName = category === 'characters' ? 'characters' : 'scenes';
  const all = await (await db()).getAll(storeName);
  return all
    .filter((x: any) => {
      if (category === 'characters') {
        return !x.category || x.category === 'characters';
      }
      if (category === 'scenes') {
        // legacy saved scenes might not have a category field
        return !x.category || x.category === 'scenes';
      }
      return x.category === category;
    })
    .sort((a, b) => b.updatedAt - a.updatedAt);
}

export async function listCharacters(): Promise<CharacterTemplate[]> {
  return (await (await db()).getAll('characters')).sort((a, b) => b.updatedAt - a.updatedAt);
}
export async function listScenes(): Promise<SceneSaver[]> {
  return (await (await db()).getAll('scenes')).sort((a, b) => b.updatedAt - a.updatedAt);
}
